import { ARDOQ_DEFAULT_LOCALE, localeList } from './locales';
import { Locale } from './types';

export const getLocale = (localeCode?: string) => {
  if (!localeCode) return undefined;

  const localeData = localeList.find(
    ({ code }) => code.toLowerCase() === localeCode.toLowerCase()
  );

  return localeData?.locale;
};

const constructCollator = (locale: Locale, options?: Intl.CollatorOptions) => {
  const supportedLocales = Intl.Collator.supportedLocalesOf(locale.code);

  return new Intl.Collator(
    supportedLocales.length ? supportedLocales : ARDOQ_DEFAULT_LOCALE.code,
    options
  );
};

const createCachedCollatorGetter = (options?: Intl.CollatorOptions) => {
  let localeCache = ARDOQ_DEFAULT_LOCALE;
  let collatorCache = constructCollator(ARDOQ_DEFAULT_LOCALE, options);

  return (locale: Locale) => {
    if (locale !== localeCache) {
      localeCache = locale;
      collatorCache = constructCollator(locale, options);
    }

    return collatorCache;
  };
};

/**
 * Collator's compare method returns a number indicating whether one string comes before, after, or is the same as the other string in sort order.
 * Strings that differ in base letters, accents and other diacritic marks, or case compare as unequal.
 * Numeric substrings are compared character by character ('foo bar 22' < 'foo bar 9').
 */
export const getStrictCollator = createCachedCollatorGetter();

/**
 * Collator's compare method returns a number indicating whether one string comes before, after, or is the same as the other string in sort order.
 * Strings that differ in base letters, accents and other diacritic marks compare as unequal.
 * Strings that differ only in case compare as equal.
 * Numeric substrings are compared character by character ('foo bar 22' < 'foo bar 9').
 */
export const getLowercaseCollator = createCachedCollatorGetter({
  sensitivity: 'accent',
});

/**
 * Collator's compare method returns a number indicating whether one string comes before, after, or is the same as the other string in sort order.
 * Strings that differ in base letters, accents and other diacritic marks, or case compare as unequal.
 * Numeric substrings are compared as numbers ('foo bar 22' > 'foo bar 9').
 */
export const getNumericCollator = createCachedCollatorGetter({
  numeric: true,
});

/**
 * Collator's compare method returns a number indicating whether one string comes before, after, or is the same as the other string in sort order.
 * Strings that differ in base letters, accents and other diacritic marks compare as unequal.
 * Strings that differ only in case compare as equal.
 * Numeric substrings are compared as numbers ('foo bar 22' > 'foo bar 9').
 */
export const getNumericLowercaseCollator = createCachedCollatorGetter({
  numeric: true,
  sensitivity: 'accent',
});

/**
 * Collator's compare method returns a number indicating whether one string comes before, after, or is the same as the other string in sort order.
 * Strings that differ in base letters compare as unequal.
 * Strings that differ only in accents and other diacritic marks, or case compare as equal.
 * Numeric substrings are compared character by character ('foo bar 22' < 'foo bar 9').
 */
export const getBaseCollator = createCachedCollatorGetter({
  sensitivity: 'base',
});

/**
 * Collator's compare method returns a number indicating whether one string comes before, after, or is the same as the other string in sort order.
 * Strings that differ in base letters compare as unequal.
 * Strings that differ only in punctuation, accents and other diacritic marks, or case compare as equal.
 * Numeric substrings are compared character by character ('foo bar 22' < 'foo bar 9').
 */
export const getInsensitiveCollator = createCachedCollatorGetter({
  sensitivity: 'base',
  ignorePunctuation: true,
});
